import React from 'react';
import { RegionalTable } from './RegionalTable';
import { getColumnDefsByType, PAGE_SIZE } from './utils';
import { STATES_MAP_CODES } from '../utils';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import ChangeTypeModal from 'Details/ChangeTypeModal/ChangeTypeModal';
import TopVendorsModal from 'Details/TopVendorsModal';
import MoveToBinModal from 'Details/MoveToBinModal/MoveToBinModal';
import CategorisationDialog from 'Explore/CategorisationDialog';
import { SPEND_TYPE } from 'Data/constants';
import {
	getInvestigateLocationsData,
	changeCategorisation,
} from 'utils/apiRequests';
import { InvestigateContext } from '../../context';
import {
	SPEND_TYPE_LABELS,
	SPEND_BY_LABELS,
	SPEND_BY_LABELS_PLURAL,
	TAXONOMY_TYPES,
	SPEND_TYPE as SPEND_TYPE_PARAM,
	SPEND_BY_PROPS,
	BREAKDOWN_BY,
	makeEmptyFromUnknown,
} from '../../utils';

export class RegionalTableContainer extends React.Component {
	state = {
		data: [],
		totalCount: 0,
		isLoading: false,
		headers: [],
		page: 1,
		sortModel: null,
		categorisationModalData: null,
		changeStatusModalData: null,
		topVendorsModalData: null,
		moveToBinModalData: null,
	};

	static contextType = InvestigateContext;

	componentDidMount() {
		this.fetchData();
	}

	componentDidUpdate(prevProps) {
		const {
			queryParams,
			dimension,
			selectedCountry,
			selectedState,
			selectedItem,
		} = this.props;

		if (
			selectedCountry !== prevProps.selectedCountry ||
			selectedState !== prevProps.selectedState ||
			selectedItem !== prevProps.selectedItem ||
			dimension.value !== prevProps.dimension.value ||
			!isEqual(queryParams, prevProps.queryParams)
		) {
			this.setState({ page: 1, sortModel: null }, this.fetchData);
		}
	}

	getSortParams = () => {
		const { headers, sortModel } = this.state;

		if (!sortModel) {
			return {};
		}

		const column = headers.find(item => item.colName === sortModel.colId);

		return {
			sortDirection: sortModel && sortModel.sort,
			sortIndex: column && column.sortIndex,
		};
	};

	getApiParams = () => {
		const {
			dimension,
			queryParams,
			region,
			selectedCountry,
			selectedState,
			spendBy,
			selectedItem,
		} = this.props;
		const { page } = this.state;
		const isPurchasing = dimension.value === 'purchasing';
		const regionFilter = `${dimension.value}Region`;
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const showState = !!STATES_MAP_CODES[selectedCountry];

		return {
			...queryParams,
			...this.getSortParams(),
			isPurchasing,
			includeState: showState,
			take: PAGE_SIZE,
			skip: PAGE_SIZE * (page - 1),
			filters: [
				...queryParams.filters,
				regionFilter,
				...(selectedCountry ? [countryFilter] : []),
				...(selectedState ? [stateFilter] : []),
				...(selectedItem ? [spendBy] : []),
			],
			filterValues: [
				...queryParams.filterValues,
				makeEmptyFromUnknown(region),
				...(selectedCountry ? [selectedCountry] : []),
				...(selectedState ? [selectedState] : []),
				...(selectedItem ? [selectedItem] : []),
			],
		};
	};

	fetchData = () => {
		const { page, data } = this.state;

		this.setState({ isLoading: true });

		return getInvestigateLocationsData(this.getApiParams())
			.then(({ headers, body, totalCount, tailSpend }) => {
				this.setState({
					data: page === 1 ? body : [...data, ...body],
					headers,
					tailSpend,
					totalCount,
					isLoading: false,
				});
			})
			.catch(() => {
				this.setState({ isLoading: false });
			});
	};

	spendFormatter = value => {
		const { currencySymbol, hideDecimals } = this.props;

		return value
			? formatSpendWithDecimalAndSymbol(
					value,
					currencySymbol,
					hideDecimals,
					2
			  ).toUpperCase()
			: '-';
	};

	getColumnDefs = () => {
		const { selectedCountry, spendType, spendBy, queryParams } = this.props;
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const { sortModel, headers } = this.state;
		const showTotalSpend = isNumber(spendType);
		const dimensionHeaders =
			queryParams.splitSpendByDimension &&
			queryParams.splitSpendByDimension !== BREAKDOWN_BY.NONE
				? headers.slice(7)
				: null;

		return getColumnDefsByType(
			SPEND_BY_LABELS_PLURAL[spendBy],
			dimensionHeaders,
			this.spendFormatter,
			showState,
			sortModel,
			showTotalSpend
		);
	};

	handleLoadMore = () => {
		const { page } = this.state;

		this.setState({ page: page + 1 }, this.fetchData);
	};

	handleSortChanged = ([sortModel]) => {
		if (sortModel) {
			this.setState({ sortModel, page: 1 }, this.fetchData);
		}
	};

	isCustomCategoryRow = row => {
		const { queryParams, spendType } = this.props;

		if (spendType != null) {
			const taxonomyType =
				spendType === SPEND_TYPE_PARAM.DIRECT
					? queryParams.directTaxonomyType
					: queryParams.indirectTaxonomyType;

			return taxonomyType === TAXONOMY_TYPES.CLIENT;
		}

		const taxonomyType = row.directSpend
			? queryParams.directTaxonomyType
			: queryParams.indirectTaxonomyType;

		return taxonomyType === TAXONOMY_TYPES.CLIENT;
	};

	handleViewTransactionsClick = row => {
		const {
			queryParams,
			showExplorePage,
			spendBy,
			dimension,
			selectedCountry,
			spendType,
		} = this.props;
		const options = {};
		const exploreHierarchy = [...queryParams.hierarchy];

		if (this.isCustomCategoryRow(row)) {
			options.isCustom = true;
		}

		if (spendType != null) {
			options.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		if (spendBy === SPEND_BY_PROPS.CATEGORY) {
			exploreHierarchy.push(row.name);
		} else {
			options.selectedDimensionKey = spendBy;
			options.selectedDimensionName = row.name;
		}

		// If show tail spend checkbox is checked, tail spend transactions is excluded into separate row.
		// In that case every row should show transactions without tail spend (isTailSpend = false)
		// If tail spend bar is clicked on the waterfall then all the rows display only tail spend (isTailSpend = true)
		// If show tail spend checkbox is unchecked the rows display all spend (isTailSpend = undefined)
		if (queryParams.getTailSpendOnly) {
			options.isTailSpend = true;
		} else if (queryParams.includeTailSpend) {
			options.isTailSpend = false;
		}

		const showState = !!STATES_MAP_CODES[selectedCountry];
		if (showState) {
			options[`${dimension.value}LocationCountry`] = selectedCountry;
			options[`${dimension.value}LocationState`] = row.location;
		} else {
			options[`${dimension.value}LocationCountry`] = row.location;
		}

		if (exploreHierarchy && exploreHierarchy.length > 0) {
			options.hierarchy = exploreHierarchy;
		}

		showExplorePage(options, row.name);
	};

	getTransactionsParams = () => {
		const { categorisationModalData } = this.state;
		const { spendBy } = this.props;

		if (!categorisationModalData) {
			return {};
		}

		return {
			filters: [spendBy],
			filterValues: [categorisationModalData.name],
		};
	};

	getContextParams = () => {
		const { selectedCountry, dimension, spendBy } = this.props;
		const { categorisationModalData } = this.state;

		if (!categorisationModalData) {
			return {};
		}

		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = categorisationModalData.location;
		const apiParams = this.getApiParams();
		const hasLocationParam = apiParams.filters.includes(locationParam);
		const apiParamsContext = {
			includeTailSpend: !apiParams.includeTailSpend,
			dateFrom: apiParams.dateFrom,
			dateTo: apiParams.dateTo,
			hierarchy: apiParams.hierarchy,
			filters: [
				...apiParams.filters,
				...(hasLocationParam ? [locationParam] : []),
				spendBy,
			],
			filterValues: [
				...apiParams.filterValues,
				...(hasLocationParam ? [locationValue] : []),
				categorisationModalData.name,
			],
		};

		return apiParamsContext;
	};

	handleChangeCategorisation = ({ taxonomy, isDirect, isAllTransactions }) => {
		const params = isAllTransactions
			? this.getTransactionsParams()
			: this.getContextParams();
		const spendType = isDirect ? SPEND_TYPE.DIRECT : SPEND_TYPE.INDIRECT;
		const data = !isDirect
			? {
					l4Code: taxonomy.aP_L4Code,
			  }
			: {
					l1Category: taxonomy.project_L1Category,
					l2Category: taxonomy.project_L2Category,
					l3Category: taxonomy.project_L3Category,
					l4Category: taxonomy.project_L4Category,
			  };

		return changeCategorisation(spendType, params, data).then(() => {
			const { refreshContent } = this.context;

			if (refreshContent) {
				refreshContent();
			}
		});
	};

	handleChangeStatus = row => {
		const {
			queryParams,
			dimension,
			region,
			selectedCountry,
			selectedState,
			spendType,
			spendBy,
		} = this.props;
		const exploreHierarchy = [...queryParams.hierarchy];

		// Regional variables
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const regionFilter = `${dimension.value}Region`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = row.location;

		// Company, region, country, state params
		const extraFilters = {
			[regionFilter]: [makeEmptyFromUnknown(region)],
			...(selectedCountry ? { [countryFilter]: [selectedCountry] } : {}),
			...(selectedState ? { [stateFilter]: [selectedState] } : {}),
			[locationParam]: [locationValue],
		};

		const data = { isPmi: true, extraFilters };

		if (spendBy === SPEND_BY_PROPS.CATEGORY) {
			data.categoryHierarchy = [...exploreHierarchy, row.name];
		} else {
			data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
			data.selectedDimensionKey = spendBy;
			data.selectedDimensionKeyTitle = SPEND_BY_LABELS[spendBy];
			data.selectedDimensionName = row.name;
		}

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		if (this.isCustomCategoryRow(row)) {
			data.includeCustomCategories = true;
		}

		this.setState({
			changeStatusModalData: data,
		});
	};

	showTopVendorsModal = row => {
		const {
			dimension,
			region,
			selectedCountry,
			selectedState,
			spendType,
			queryParams,
			spendBy,
		} = this.props;
		const hierarchy = [...queryParams.hierarchy];

		// Regional variables
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const regionFilter = `${dimension.value}Region`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = row.location;

		// Company, region, country, state params
		const extraFilters = {
			[regionFilter]: [makeEmptyFromUnknown(region)],
			...(selectedCountry ? { [countryFilter]: [selectedCountry] } : {}),
			...(selectedState ? { [stateFilter]: [selectedState] } : {}),
			[locationParam]: [locationValue],
			...(spendType != null ? { spendType: [spendType] } : {}),
		};

		const data = {
			hierarchy,
			// If show tail spend is checked we need to exclude tail spend from row
			includeTailSpend:
				!queryParams.getTailSpendOnly && !queryParams.includeTailSpend,
			name: row.name,
			extraFilters,
			getTailSpendOnly: queryParams.getTailSpendOnly,
		};

		if (spendBy === SPEND_BY_PROPS.CATEGORY) {
			data.hierarchy = [...data.hierarchy, row.name];
		} else {
			data.extraFilters = {
				...data.extraFilters,
				[spendBy]: [row.name],
			};
		}

		if (this.isCustomCategoryRow(row)) {
			data.includeCustomCategories = true;
		}

		this.setState({
			topVendorsModalData: data,
		});
	};

	hideTopVendorsModal = () => {
		this.setState({
			topVendorsModalData: null,
		});
	};

	handleMoveToBin = row => {
		const {
			spendBy,
			dimension,
			region,
			selectedCountry,
			selectedState,
			spendType,
			queryParams,
		} = this.props;
		const exploreHierarchy = [...queryParams.hierarchy];

		// Regional variables
		const showState = !!STATES_MAP_CODES[selectedCountry];
		const countryFilter = `${dimension.value}Country`;
		const stateFilter = `${dimension.value}State`;
		const regionFilter = `${dimension.value}Region`;
		const locationParam = showState ? stateFilter : countryFilter;
		const locationValue = row.location;

		// Company, region, country, state params
		const extraFilters = {
			[regionFilter]: [makeEmptyFromUnknown(region)],
			...(selectedCountry ? { [countryFilter]: [selectedCountry] } : {}),
			...(selectedState ? { [stateFilter]: [selectedState] } : {}),
			[locationParam]: [locationValue],
		};

		const data = { isPmi: true, extraFilters };

		if (spendBy === SPEND_BY_PROPS.CATEGORY) {
			data.categoryHierarchy = [...exploreHierarchy, row.name];
		} else {
			data.hierarchy = exploreHierarchy.length > 0 ? exploreHierarchy : null;
			data.selectedDimensionKey = spendBy;
			data.selectedDimensionKeyTitle = SPEND_BY_LABELS[spendBy];
			data.selectedDimensionName = row.name;
		}

		if (spendType != null) {
			data.spendType = SPEND_TYPE_LABELS[spendType].toUpperCase();
		}

		if (this.isCustomCategoryRow(row)) {
			data.includeCustomCategories = true;
		}

		this.setState({
			moveToBinModalData: data,
		});
	};

	showCategorisationModal = categorisationModalData => {
		this.setState({ categorisationModalData });
	};

	hideCategorisationModal = () => {
		this.setState({ categorisationModalData: null });
	};

	getTailSpendRow = () => {
		const { tailSpend } = this.state;

		if (!tailSpend) {
			return [];
		}

		return [
			{
				...tailSpend,
				name: 'Tail spend',
				location: '-',
			},
		];
	};

	render() {
		const {
			isLoading,
			data,
			totalCount,
			categorisationModalData,
			changeStatusModalData,
			topVendorsModalData,
			moveToBinModalData,
		} = this.state;
		const { spendBy, hideDecimals } = this.props;
		const { refreshContent } = this.context;

		const apiParams = this.getApiParams();
		return (
			<React.Fragment>
				<RegionalTable
					isLoading={isLoading}
					data={data}
					pinnedBottomRowData={this.getTailSpendRow()}
					totalCount={totalCount}
					loadMore={this.handleLoadMore}
					onSortChanged={this.handleSortChanged}
					columnDefs={this.getColumnDefs()}
					showCategorisationModal={
						spendBy !== SPEND_BY_PROPS.CATEGORY
							? this.showCategorisationModal
							: null
					}
					onViewTransactionsClick={this.handleViewTransactionsClick}
					onTopVendorsClick={this.showTopVendorsModal}
					onChangeStatus={this.handleChangeStatus}
					onMoveToBin={this.handleMoveToBin}
					apiParams={apiParams}
				/>
				{topVendorsModalData && (
					<TopVendorsModal
						isPmi
						onClose={this.hideTopVendorsModal}
						hideDecimals={hideDecimals}
						{...topVendorsModalData}
					/>
				)}
				{categorisationModalData && (
					<CategorisationDialog
						name={categorisationModalData.name}
						onClose={this.hideCategorisationModal}
						onSave={this.handleChangeCategorisation}
						contextParams={this.getContextParams()}
						allTransactionsParams={this.getTransactionsParams()}
					/>
				)}
				{changeStatusModalData && (
					<ChangeTypeModal
						isOpen={changeStatusModalData}
						isHarmonized
						{...changeStatusModalData}
						onClose={() => {
							this.setState({ changeStatusModalData: null });
						}}
						onSubmit={refreshContent}
					/>
				)}
				{moveToBinModalData && (
					<MoveToBinModal
						isOpen={moveToBinModalData}
						isHarmonized
						{...moveToBinModalData}
						onClose={() => {
							this.setState({ moveToBinModalData: null });
						}}
						onSubmit={refreshContent}
					/>
				)}
			</React.Fragment>
		);
	}
}

export default RegionalTableContainer;
